import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToastNotification } from 'hooks/useToastNotification';
import { useDeleteTicketImport, useTicketImportsList, useUploadTicketImport } from '../../hooks';
import { getTicketImport } from '../../services';
import { useWizard } from '../../store';
import { TicketImport } from '../../types';
import {
	Button,
	Card,
	CardDescription,
	CardTitle,
	DropZone,
	Footer,
	Input,
	Label,
	Row,
	ScrollContainer,
	Table,
} from '../../TicketImporter.styles';

// Statuses we treat as "resumable": the wizard can pick them back up where
// they were left. COMPLETED imports are also returned by the list but we
// only show resumable ones to avoid cluttering the entry point.
const RESUMABLE_STATUSES = ['DRAFT', 'MAPPED', 'VALIDATED', 'FAILED', 'COMMITTING'];

const formatRelative = (iso?: string): string => {
	if (!iso) return '';
	const d = new Date(iso);
	if (isNaN(d.getTime())) return iso;
	return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

// Each status maps to the wizard step where the user left off. We hop
// to the next pending step so they don't have to re-do work they finished.
const stepForStatus = (status: string, scopeMode?: string): string => {
	switch (status) {
		case 'DRAFT':
			return '#2'; // CSV uploaded, scope not picked yet
		case 'MAPPED':
			// User has saved mapping at some point; jump to the events step
			// (per-row) or options (fixed scope).
			return scopeMode === 'per_row' ? '#4' : '#5';
		case 'VALIDATED':
			return '#7'; // preview ran successfully
		case 'COMMITTING':
		case 'FAILED':
			return '#8'; // commit step shows the failure / lets retry
		default:
			return '#2';
	}
};

export const UploadStep = (): JSX.Element => {
	const history = useHistory();
	const { pathname } = useLocation();
	const queryClient = useQueryClient();
	const { state, dispatch } = useWizard();
	const { showNotification, TYPES } = useToastNotification();
	const inputRef = useRef<HTMLInputElement>(null);
	const [dragging, setDragging] = useState(false);
	const [sourcePlatform, setSourcePlatform] = useState(state.sourcePlatform);

	const { data: drafts, isLoading: draftsLoading } = useTicketImportsList({
		status: RESUMABLE_STATUSES.join(','),
		limit: 10,
	});

	const { mutate: uploadMutate, isLoading: uploadLoading } = useUploadTicketImport({
		onSuccess: (data) => {
			dispatch({ type: 'setUpload', payload: data });
			dispatch({ type: 'patch', payload: { sourcePlatform } });
			showNotification({ type: TYPES.SUCCESS, message: 'CSV parsed' });
			queryClient.refetchQueries({ queryKey: ['ticket-imports'] });
			history.push(`${pathname}#2`);
		},
		onError: (err: any) => {
			const msg = err?.response?.data?.error || 'Upload failed';
			showNotification({ type: TYPES.ERROR, message: msg });
		},
	});

	const { mutate: discardMutate } = useDeleteTicketImport({
		// Optimistic: drop the row from any cached list immediately, then
		// confirm with a server refetch on success (or restore on error).
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey: ['ticket-imports'] });
			const snapshots = queryClient.getQueriesData<TicketImport[]>({ queryKey: ['ticket-imports'] });
			queryClient.setQueriesData<TicketImport[]>({ queryKey: ['ticket-imports'] }, (old) =>
				(old ?? []).filter((imp) => imp.ID !== id)
			);
			return { snapshots };
		},
		onError: (err: any, _id, context: any) => {
			if (context?.snapshots) {
				for (const [key, data] of context.snapshots) {
					queryClient.setQueryData(key, data);
				}
			}
			const msg = err?.response?.data?.error || 'Could not discard import';
			showNotification({ type: TYPES.ERROR, message: msg });
		},
		onSuccess: () => {
			showNotification({ type: TYPES.SUCCESS, message: 'Import discarded' });
		},
		onSettled: () => {
			queryClient.refetchQueries({ queryKey: ['ticket-imports'] });
		},
	});

	const handleFile = (file?: File | null) => {
		if (!file) return;
		uploadMutate({ file, sourcePlatform });
	};

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		handleFile(e.target.files?.[0]);
		if (inputRef.current) inputRef.current.value = '';
	};

	const onDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setDragging(false);
		handleFile(e.dataTransfer.files?.[0]);
	};

	const onResume = async (imp: TicketImport) => {
		try {
			// Fetch the full record to ensure we have every field (the list
			// endpoint returns the same shape, but we go through getTicketImport
			// to give the user feedback if the record was deleted concurrently).
			const fresh = await getTicketImport(imp.ID);
			dispatch({ type: 'hydrateFromImport', payload: fresh });
			history.push(`${pathname}${stepForStatus(fresh.status, fresh.scope_mode)}`);
		} catch (err: any) {
			const msg = err?.response?.data?.error || 'Could not resume import';
			showNotification({ type: TYPES.ERROR, message: msg });
		}
	};

	const onDiscard = (imp: TicketImport) => {
		if (!window.confirm(`Discard the import "${imp.file_name}"? This cannot be undone.`)) return;
		discardMutate(imp.ID);
	};

	const showDrafts = !draftsLoading && drafts && drafts.length > 0;

	return (
		<>
			{showDrafts && (
				<Card>
					<CardTitle>Resume a draft</CardTitle>
					<CardDescription>
						You have unfinished imports. Pick one to continue where you left off, or start a fresh one below.
					</CardDescription>
					<ScrollContainer style={{ maxHeight: 300 }}>
						<Table>
							<thead>
								<tr>
									<th>File</th>
									<th>Status</th>
									<th>Rows</th>
									<th>Created</th>
									<th style={{ width: 220 }}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{drafts?.map((imp) => (
									<tr key={imp.ID}>
										<td>
											<strong>{imp.file_name}</strong>
											{imp.source_platform ? (
												<small style={{ color: '#9ca3af' }}> · {imp.source_platform}</small>
											) : null}
										</td>
										<td>
											<small style={{ fontFamily: 'monospace' }}>{imp.status}</small>
										</td>
										<td>{imp.row_count.toLocaleString()}</td>
										<td style={{ fontSize: 12, color: '#6b7280' }}>{formatRelative(imp.CreatedAt)}</td>
										<td>
											<div style={{ display: 'flex', gap: 8 }}>
												<Button variant="secondary" onClick={() => onResume(imp)}>
													Resume
												</Button>
												<Button variant="danger" onClick={() => onDiscard(imp)}>
													Discard
												</Button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					</ScrollContainer>
				</Card>
			)}

			<Card>
				<CardTitle>1. Upload CSV</CardTitle>
				<CardDescription>
					Upload the export from the external ticketing platform. The file is parsed on the server; you can adjust
					column mappings and event resolution in the next steps before any tickets are created.
				</CardDescription>

				<Row>
					<Label>
						Source platform <span style={{ color: '#9ca3af' }}>(optional)</span>
						<Input
							type="text"
							placeholder="e.g. ticketmaster, axs, sst"
							value={sourcePlatform}
							onChange={(e) => setSourcePlatform(e.target.value)}
						/>
					</Label>
				</Row>

				<DropZone
					dragging={dragging}
					onClick={() => inputRef.current?.click()}
					onDragOver={(e) => {
						e.preventDefault();
						setDragging(true);
					}}
					onDragLeave={() => setDragging(false)}
					onDrop={onDrop}
				>
					{uploadLoading ? (
						<p>Uploading…</p>
					) : (
						<>
							<p style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>
								Drag &amp; drop your CSV here, or click to browse
							</p>
							<p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>
								Accepts .csv files of any size. Common platforms (Ticketmaster, AXS, SST) are auto-detected.
							</p>
						</>
					)}
					<input ref={inputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={onChange} />
				</DropZone>

				{state.upload && (
					<div style={{ marginTop: 16, fontSize: 14 }}>
						<strong>File loaded:</strong> {state.upload.file_name} ({state.upload.row_count.toLocaleString()} rows,{' '}
						{state.upload.headers.length} columns)
					</div>
				)}
			</Card>

			<Footer>
				<span />
				<Button onClick={() => history.push(`${pathname}#2`)} disabled={!state.upload}>
					Continue →
				</Button>
			</Footer>
		</>
	);
};
